import Decimal from 'decimal.js';
import { RefObject } from 'react';

import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { EditTestamentaryEntitySection } from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal/EditTestamentaryEntitySplitScreen.types';
import { EntityType } from '@/modules/entities/types/EntityType';
import {
  DispositiveProvisionDispositionKind,
  DispositiveProvisionTransferTaxKind,
  TransferMethod,
} from '@/types/schema';
import { PathsOf } from '@/types/subform';

import { DispositionScheme } from '../dispositiveProvisions.types';
import { DISPOSITIVE_PROVISIONS_FORM_NAMESPACE } from './DispositiveProvisionsForm.constants';

export interface DispositiveProvisionsFormProps {
  headerRef: RefObject<HTMLDivElement | null>;
  footerRef: RefObject<HTMLDivElement | null>;
  onClose: () => void;
  onAfterSubmit: (afterSubmitSection?: EditEntitySection) => void;
  firstDeathClientId: string;
  /**
   * Called after any successful persist (including diagram-tile saves that
   * don't close the modal). Lets a host refresh derived data on close — the
   * presentation designer uses it to know an edit landed so it refreshes the
   * entity-overview context (e.g. the dispositions table) rather than leaving
   * it stale until a full page reload.
   */
  onDidSave?: (options: { isSwitchingTarget: boolean }) => void;
  /** Handler for section switching when variant is 'editTestamentaryEntity' */
  onTestamentaryEntitySectionSubmit?: (
    afterSubmitSection?: EditTestamentaryEntitySection
  ) => void;
}

export enum RecipientKind {
  Individual = 'individual',
  Entity = 'entity',
  Organization = 'organization',
  TestamentaryEntity = 'testamentaryEntity',
  SurvivingSpouse = 'survivingSpouse',
}

export enum DispositiveProvisionsTemplateSlotKind {
  Generic = 'generic',
  Pourover = 'pourover',
  OutrightSurvivingSpouse = 'outrightSurvivingSpouse',
  OutrightDisclaimer = 'outrightDisclaimer',
  MaritalTrust = 'maritalTrust',
  MaritalDisclaimer = 'maritalDisclaimer',
  MaritalClaytonQtip = 'maritalClaytonQtip',
  MaritalBypass = 'maritalBypass',
  AbSurvivor = 'abSurvivor',
  AbBypass = 'abBypass',
  AbGstExempt = 'abGstExempt',
  AbGstNonExempt = 'abGstNonExempt',
  AbcCreditShelter = 'abcCreditShelter',
  AbcFederalExemptStateNonExempt = 'abcFederalExemptStateNonExempt',
  AbcGstExempt = 'abcGstExempt',
  AbcGstNonExempt = 'abcGstNonExempt',
}

export interface DispositiveProvisionsFormRecipient {
  _templateSlotKind?: DispositiveProvisionsTemplateSlotKind;
  recipientId: string | null;
  recipientKind: RecipientKind | null;
  // The disposition kind can be set from a button group or from a select input
  // if the user selects "f(x)" from the button group
  dispositionKind_ButtonGroup:
    | DispositiveProvisionDispositionKind.Percentage
    | DispositiveProvisionDispositionKind.Amount
    | 'other'
    | 'asset';
  dispositionKind_Select:
    | DispositiveProvisionDispositionKind.AnythingLeftOver
    | DispositiveProvisionDispositionKind.LesserOfRemainingStateOrFederalExemption
    | DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantorInExcessOfLifetimeExclusion
    | DispositiveProvisionDispositionKind.RemainingLifetimeExclusionOfGrantor
    | DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantor
    | DispositiveProvisionDispositionKind.RemainingStateExemption
    | DispositiveProvisionDispositionKind.RemainingFederalLifetimeExemptionInExcessOfStateExemption
    | '';
  dispositionAmount: Decimal | null;
  dispositionPercentage: Decimal | null;
  dispositionFormulaicPercentage?: Decimal | null;
  notes: string;
  transferTaxKind: DispositiveProvisionTransferTaxKind | '';
  // _dispositiveProvisionId is used to link the recipient to a dispositive provision
  // for deletion scenarios; it's never set for new recipients or interacted with via the form
  _dispositiveProvisionId: string | null;
  // Transient ordering hint captured from an AI suggestion's `dispositionOrder`
  // so that accepting suggestions out of order still sorts recipients into the
  // document's order. Not persisted directly — submit reassigns `dispositionOrder`
  // by array index (see buildSequentialProvisionInputs).
  _suggestedDispositionOrder?: number | null;
  bearsTax?: boolean;
  // Max distribution amount - caps the percentage distribution at a specific dollar amount
  dispositionLimit?: Decimal | null;
  // When true, the disposition limit will grow by the inflation rate in the growth profile
  growByInflation?: boolean;
  // How the transfer is made to the recipient. IN_KIND preserves asset growth characteristics,
  // LIQUIDATE_TO_CASH sells the asset. Non-asset DPs default to LIQUIDATE_TO_CASH.
  transferMethod?: TransferMethod;
  // Asset-level DP fields: IDs of selected assets for asset-specific distributions
  assetGroupIds: string[];
  assetStaticIds: string[];
  insurancePolicyIds: string[];
  ownershipStakeIds: string[];
  // Percentage of the selected asset(s) to distribute (0-100, default 100)
  dispositionAssetPercentage: Decimal | null;
}

// Indicates that the recipient includes a selected recipient from the dropdown
export interface RecipientWithId extends DispositiveProvisionsFormRecipient {
  recipientId: string;
}

export interface DispositiveProvisionsFormShape {
  [DISPOSITIVE_PROVISIONS_FORM_NAMESPACE]: DispositiveProvisionsForm_DispositionScenario;
}

export interface DispositiveProvisionsForm_DispositionScenario {
  // dispositionScenarioId will be present if there's already an existing disposition scenario
  // that should be updated and null if it needs to be created
  dispositionScenarioId: string | null;
  selectedFirstPrimaryClientDeathId: string | null;
  dispositionScheme: DispositionScheme;
  recipients: DispositiveProvisionsFormRecipient[];
  templateId?: string | null;
  _dispositionScenarioAssociatedWaterfallId: string | null;
  _hasBeenReviewed: boolean;
  /**
   * This field will only be set when submitting via the V2 form.
   */
  _residuaryPaysAllTaxes: boolean | null;
  transferMethod?: TransferMethod | null;
}

export type DispositiveProvisionsFormPaths =
  PathsOf<DispositiveProvisionsFormShape>;

export enum DispositiveProvisionsModalDetailsType {
  ENTITY = 'entity',
  CLIENT_PROFILE = 'clientProfile',
  TESTAMENTARY_ENTITY = 'testamentaryEntity',
}

export type DispositiveProvisionsModalDetails =
  | DispositiveProvisionsModalDetails_Entity
  | DispositiveProvisionsModalDetails_ClientProfile
  | DispositiveProvisionsModalDetails_TestamentaryEntity;

interface DispositiveProvisionsModalDetails_Entity {
  type: DispositiveProvisionsModalDetailsType.ENTITY;
  isFromTemplateEditor?: boolean;
  firstDeathClientId: string;
  entityDisplayName: string;
  entityType: EntityType;
  entityId: string;
  entitySubtypeId: string;
}

interface DispositiveProvisionsModalDetails_ClientProfile {
  type: DispositiveProvisionsModalDetailsType.CLIENT_PROFILE;
  isFromTemplateEditor?: boolean;
  displayName: string;
  clientProfileId: string;
  firstDeathClientId: string;
  /**
   * We specifically need to pass valueToDistribute here because it's computed based on
   * a waterfall, and includes a combination of business assets and received assets.
   */
  valueToDistribute: Decimal;
}

interface DispositiveProvisionsModalDetails_TestamentaryEntity {
  type: DispositiveProvisionsModalDetailsType.TESTAMENTARY_ENTITY;
  isFromTemplateEditor?: boolean;
  entityDisplayName: string;
  testamentaryEntityId: string;
  firstDeathClientId: string;
}
